"use client"

import { useEffect, useState } from "react"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { Check, Loader2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { MAX_REPORT_DETAILS, REPORT_REASONS, type ReportType } from "@/lib/moderation"
import { createClient } from "@/lib/supabase/client"
import { cn } from "@/lib/utils"

// ============================================================
// BOOTH — reporting something you are looking at.
//
// The messages page has had report + block since 1033; nothing else did. A
// person looking at a barber's portfolio or a shop listing — which is most of
// what this app shows, and all of what a signed-out visitor sees — had no way
// to say anything about it without first getting into a thread with whoever
// posted it. That is the gap App Store guideline 1.2 is about, and it is why
// this dialog is a shared component rather than more state on one page.
//
// It files through /api/reports (same insert, same RLS, plus the email that
// puts the report in front of a person) and keeps its own result state, so a
// host page needs no toast wiring of its own: two props and a trigger.
// ============================================================

interface ReportDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The auth.users id the report is filed against: the barber, or a shop's
   *  owner. reports.reported_id is a user, whatever the type says. */
  reportedId: string
  type: ReportType
  /** Named in the title, so the reporter can see they picked the right thing. */
  subjectName: string
  /** Whatever helps a moderator find it again — shop id, slug, the url. */
  context?: Record<string, unknown>
  /** Offer Block under the report. Only meaningful for a person. */
  canBlock?: boolean
}

export function ReportDialog({
  open,
  onOpenChange,
  reportedId,
  type,
  subjectName,
  context,
  canBlock = false,
}: ReportDialogProps) {
  const pathname = usePathname()
  const [reason, setReason] = useState<string | null>(null)
  const [details, setDetails] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [blocking, setBlocking] = useState(false)
  const [blocked, setBlocked] = useState(false)
  // null = not looked yet. The dialog is reachable signed out (every page it
  // sits on is public), and the route answers 401 — better to say so before
  // someone types out what happened than after.
  const [signedIn, setSignedIn] = useState<boolean | null>(null)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    void (async () => {
      const {
        data: { user },
      } = await createClient().auth.getUser()
      if (!cancelled) setSignedIn(Boolean(user))
    })()
    return () => {
      cancelled = true
    }
  }, [open])

  const reset = () => {
    setReason(null)
    setDetails("")
    setSent(false)
    setError(null)
    setBlocked(false)
  }

  const close = () => {
    onOpenChange(false)
    // After the dialog has animated out, so nothing visibly resets under it.
    setTimeout(reset, 200)
  }

  const submit = async () => {
    if (!reason || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      const res = await fetch("/api/reports", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          reportedId,
          type,
          reason,
          details: details.trim() || undefined,
          context: { ...(context ?? {}), path: pathname },
        }),
      })
      if (res.ok) {
        setSent(true)
      } else {
        // The route's own wording is written for the person reading it.
        const body = (await res.json().catch(() => null)) as { error?: string } | null
        console.error("[report] failed:", res.status, body)
        setError(body?.error || "We could not file that. Please try again.")
      }
    } catch (err) {
      console.error("[report] failed:", err)
      setError("We could not file that. Please try again.")
    }
    setSubmitting(false)
  }

  const block = async () => {
    if (blocking) return
    setBlocking(true)
    setError(null)
    const supabase = createClient()
    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (!user) {
      setSignedIn(false)
      setBlocking(false)
      return
    }
    // insert() returns its failure rather than throwing — the messages page
    // ignored this once and blocking quietly did nothing.
    const { error: blockError } = await supabase
      .from("blocked_users")
      .insert({ blocker_id: user.id, blocked_id: reportedId })
    setBlocking(false)
    // Already blocked (the pair is unique) is the state they asked for.
    if (blockError && blockError.code !== "23505") {
      console.error("[report] block failed:", blockError)
      setError("We could not block them. Try again in a moment.")
      return
    }
    setBlocked(true)
  }

  const title = type === "listing" ? `Report ${subjectName}` : `Report ${subjectName || "this person"}`

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="max-w-md rounded-[2rem]">
        {signedIn === false ? (
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <DialogTitle className="text-base font-medium">Sign in to report</DialogTitle>
            <DialogDescription className="max-w-[34ch] text-sm leading-relaxed">
              Reports go to a person on our side, so we need to know who sent one. It takes a moment.
            </DialogDescription>
            <Button asChild className="mt-2 rounded-full">
              <Link href={`/auth/login?redirect=${encodeURIComponent(pathname || "/")}`}>Sign in</Link>
            </Button>
          </div>
        ) : blocked ? (
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/15 ring-1 ring-emerald-400/30">
              <Check className="h-6 w-6 text-emerald-500" strokeWidth={2} />
            </span>
            <DialogTitle className="text-base font-medium">{subjectName} is blocked</DialogTitle>
            <DialogDescription className="max-w-[34ch] text-sm leading-relaxed">
              They can&apos;t message you. Undo it from Blocked at the top of your messages.
            </DialogDescription>
            <Button className="mt-2 rounded-full" onClick={close}>
              Done
            </Button>
          </div>
        ) : sent ? (
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/15 ring-1 ring-emerald-400/30">
              <Check className="h-6 w-6 text-emerald-500" strokeWidth={2} />
            </span>
            <DialogTitle className="text-base font-medium">Report sent</DialogTitle>
            <DialogDescription className="max-w-[34ch] text-sm leading-relaxed">
              We read every one.
              {canBlock ? " If you want them to stop messaging you now, block them too." : ""}
            </DialogDescription>
            <div className="mt-2 flex items-center gap-2">
              {canBlock && (
                <Button variant="outline" className="rounded-full" onClick={block} disabled={blocking}>
                  {blocking ? <Loader2 className="h-4 w-4 animate-spin" /> : "Block"}
                </Button>
              )}
              <Button className="rounded-full" onClick={close}>
                Done
              </Button>
            </div>
            {error && <p className="text-xs text-destructive">{error}</p>}
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{title}</DialogTitle>
              <DialogDescription>
                This goes to us, not to them.
                {canBlock ? " Blocking is separate — report tells us, block stops the messages." : ""}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">What&apos;s wrong?</Label>
                <div className="flex flex-wrap gap-2">
                  {REPORT_REASONS.map((r) => (
                    <button
                      key={r}
                      type="button"
                      onClick={() => setReason(r)}
                      className={cn(
                        "rounded-full border px-3 py-1.5 text-xs transition-colors",
                        reason === r
                          ? "border-foreground bg-foreground text-background"
                          : "border-border text-muted-foreground hover:text-foreground",
                      )}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="report-details" className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Anything else (optional)
                </Label>
                <Textarea
                  id="report-details"
                  value={details}
                  onChange={(e) => setDetails(e.target.value)}
                  placeholder={
                    type === "listing"
                      ? "What's wrong with this listing, or what you want us to look at."
                      : "What they posted, or what you want us to look at."
                  }
                  className="min-h-24 rounded-2xl"
                  maxLength={MAX_REPORT_DETAILS}
                />
              </div>

              {error && <p className="text-xs text-destructive">{error}</p>}
            </div>

            <DialogFooter className="gap-2 sm:gap-2">
              {canBlock && (
                <Button
                  variant="ghost"
                  className="mr-auto rounded-full text-muted-foreground hover:text-foreground"
                  onClick={block}
                  disabled={blocking}
                >
                  {blocking ? "Blocking…" : "Block"}
                </Button>
              )}
              <Button variant="ghost" className="rounded-full" onClick={close}>
                Cancel
              </Button>
              <Button className="rounded-full" onClick={submit} disabled={!reason || submitting}>
                {submitting ? "Sending…" : "Send report"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
